import { ExternalLink, LockOpen, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatFeedbackDate } from '@/components/Gestores/gestorFeedbackConstants'
import { useAuthenticatedAssetUrl } from '@/components/Gestores/gestorFeedbackEvidenceUtils'
import type { GestorEquipeFeedback } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type Props = {
  items: GestorEquipeFeedback[]
  onEdit: (item: GestorEquipeFeedback) => void
  onLiberar: (feedbackId: string) => Promise<void>
  onDelete: (feedbackId: string) => Promise<void>
}

function EvidencePreview({ item }: { item: GestorEquipeFeedback }) {
  const previewUrl = useAuthenticatedAssetUrl(item.evidenciaUrl)

  if (item.evidenciaLink) {
    return (
      <a
        href={item.evidenciaLink}
        target='_blank'
        rel='noreferrer'
        className='inline-flex items-center gap-1 text-xs text-accent hover:underline'
      >
        <ExternalLink size={12} />
        Abrir link
      </a>
    )
  }

  if (previewUrl) {
    return (
      <a href={previewUrl} target='_blank' rel='noreferrer' className='block max-w-[120px]'>
        <img src={previewUrl} alt='Evidência' className='h-14 w-full rounded border border-border object-cover' />
      </a>
    )
  }

  return <span className='text-xs text-text-muted'>—</span>
}

export function GestorFeedbackDocumentTable({ items, onEdit, onLiberar, onDelete }: Props) {
  if (items.length === 0) {
    return <p className='text-sm text-text-muted'>Nenhum registro documentado ainda.</p>
  }

  return (
    <div className='overflow-x-auto rounded-xl border border-border'>
      <table className='min-w-[960px] w-full text-left text-sm'>
        <thead className='bg-white/[0.04] text-xs uppercase tracking-wide text-text-muted'>
          <tr>
            <th className='px-3 py-3'>Quem pontuou</th>
            <th className='px-3 py-3'>Data</th>
            <th className='px-3 py-3'>Área</th>
            <th className='px-3 py-3 min-w-[220px]'>Situação</th>
            <th className='px-3 py-3'>Evidência</th>
            <th className='px-3 py-3'>Reportado</th>
            <th className='px-3 py-3'>Data report</th>
            <th className='px-3 py-3 min-w-[180px]'>Observações</th>
            <th className='px-3 py-3'>Status</th>
            <th className='px-3 py-3 text-right'>Ações</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className='border-t border-border align-top'>
              <td className='px-3 py-3 font-medium text-text-primary'>{item.author.name}</td>
              <td className='px-3 py-3 text-text-secondary'>{formatFeedbackDate(item.dataFeedback ?? item.createdAt)}</td>
              <td className='px-3 py-3'>
                <span className='rounded-full bg-violet-500/10 px-2 py-0.5 text-xs font-medium text-violet-300'>
                  {item.areaAtuacao ?? '—'}
                </span>
              </td>
              <td className='px-3 py-3 text-text-secondary whitespace-pre-wrap'>{item.situacao ?? item.conteudo}</td>
              <td className='px-3 py-3'>
                <EvidencePreview item={item} />
              </td>
              <td className='px-3 py-3'>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    item.reportado ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/[0.06] text-text-muted',
                  )}
                >
                  {item.reportado ? 'Sim' : 'Não'}
                </span>
              </td>
              <td className='px-3 py-3 text-text-secondary'>{formatFeedbackDate(item.dataReport)}</td>
              <td className='px-3 py-3 text-text-secondary whitespace-pre-wrap'>{item.observacoesReporte ?? '—'}</td>
              <td className='px-3 py-3'>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    item.liberadoParaPj ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400',
                  )}
                >
                  {item.liberadoParaPj ? 'Liberado' : 'Rascunho'}
                </span>
              </td>
              <td className='px-3 py-3'>
                {!item.liberadoParaPj && (
                  <div className='flex justify-end gap-1'>
                    <Button type='button' size='sm' variant='secondary' onClick={() => onEdit(item)}>
                      <Pencil size={14} />
                    </Button>
                    <Button type='button' size='sm' onClick={() => void onLiberar(item.id)}>
                      <LockOpen size={14} />
                    </Button>
                    <Button type='button' size='sm' variant='destructive' onClick={() => void onDelete(item.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
